import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Review {
  id: string
  employee_name: string
  reviewer: string
  review_type: string
  review_period: string
  status: string
  overall_rating: string
  score: number
}

interface Goal {
  id: string
  employee_name: string
  title: string
  goal_type: string
  status: string
  progress_percent: number
  priority: string
}

export default function PerformancePage() {
  const [reviews, setReviews] = useState<Review[]>([])
  const [goals, setGoals] = useState<Goal[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [revRes, goalRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/performance/reviews'),
      fetch(API_BASE + '/performance/goals'),
      fetch(API_BASE + '/performance/statistics')
    ])
    if (revRes.ok) setReviews((await revRes.json()).reviews || [])
    if (goalRes.ok) setGoals((await goalRes.json()).goals || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'scheduled': return 'bg-yellow-100 text-yellow-800'
      case 'at_risk': return 'bg-red-100 text-red-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'outstanding': case 'exceeds_expectations': return 'text-green-600'
      case 'meets_expectations': return 'text-blue-600'
      case 'needs_improvement': return 'text-yellow-600'
      case 'below_expectations': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Performance Reviews</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Review</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Reviews</div>
              <div className="text-2xl font-bold">{statistics.total_reviews}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-2xl font-bold text-green-600">{statistics.completed_reviews}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Score</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.avg_rating_score}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Goals Completed</div>
              <div className="text-2xl font-bold">{statistics.goals_completed}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Goals At Risk</div>
              <div className="text-2xl font-bold text-red-600">{statistics.goals_at_risk}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Reviews</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reviews.slice(0, 10).map(rev => (
                  <tr key={rev.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{rev.employee_name}</div>
                      <div className="text-xs text-gray-500">Reviewer: {rev.reviewer}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{rev.review_period}</div>
                      <div className="text-xs text-gray-500 capitalize">{rev.review_type.replace(/_/g, ' ')}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-bold ${getRatingColor(rev.overall_rating)}`}>{rev.score}/5</div>
                      <div className="text-xs text-gray-500 capitalize">{rev.overall_rating.replace(/_/g, ' ')}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(rev.status)}`}>{rev.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Goals</h3>
            <div className="space-y-3">
              {goals.filter(g => g.status !== 'completed').slice(0, 10).map(goal => (
                <div key={goal.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{goal.title}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(goal.status)}`}>{goal.status.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-xs text-gray-500">{goal.employee_name}</div>
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${goal.progress_percent}%` }} />
                      </div>
                      <span className="text-xs">{goal.progress_percent}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
